/**
 * The /runs pager position lives in the URL (`?page=N`, 1-based) rather than
 * in component state, so Back from a run detail lands on the page the operator
 * left instead of resetting to page 1, and a page can be linked to.
 *
 * Anything that isn't a plain positive integer — a hand-edited `?page=abc`,
 * `0`, `-2`, `1.5`, `1e3` — reads as the first page rather than an error, and
 * so does a page whose offset would pass MAX_SAFE_INTEGER, which the API's
 * `offset` schema rejects with a 400 (PR #128).
 */
export function parsePageParam(raw: string | null, pageSize: number): number {
  if (raw === null || !/^[1-9]\d*$/.test(raw)) return 0;
  const page = Number(raw) - 1;
  return Number.isSafeInteger(page * pageSize) ? page : 0;
}

/**
 * `current` with only `page` set to a 0-based page; page 0 drops the param for
 * a clean URL. Other params are carried over so future URL filters survive
 * Prev/Next.
 */
export function pageSearch(current: URLSearchParams, page: number): URLSearchParams {
  const next = new URLSearchParams(current);
  if (page > 0) next.set("page", String(page + 1));
  else next.delete("page");
  return next;
}

/**
 * The /runs query asks for one row past the page (`pageSize + 1`) so "is there
 * a next page" is answered by the API rather than guessed. Guessing from "this
 * page came back full" enables Next whenever the total is an exact multiple of
 * the page size, and that click lands on an empty "No more runs." page.
 */
export function splitPage<T>(fetched: readonly T[], pageSize: number): { rows: T[]; hasNext: boolean } {
  return { rows: fetched.slice(0, pageSize), hasNext: fetched.length > pageSize };
}
